import auth0 from 'auth0-js'
import * as Sentry from '@sentry/browser'
import config from '../config'
import { useAppStore, type User } from '../stores/app'

interface AuthResult {
  idToken?: string
  accessToken?: string
  idTokenPayload?: any
  expiresIn?: number
  memberId?: string
}

interface Profile {
  name?: string
  expires?: Date
  exp?: number
  [key: string]: any
}

const webAuth = new auth0.WebAuth({
  domain: config.domain,
  redirectUri: `${window.location.origin}/callback`,
  clientID: import.meta.env.VITE_AUTH_CLIENT_ID,
  responseType: 'token id_token',
  scope: 'openid profile email',
  audience: 'https://api.mallshoplink.com/',
})

const loginStatusKey = 'loggedIn'
const loginNameKey = 'loggedInName'
const dataFromTokenKey = 'dataFromToken'
const tokenLoginPath = '/auth/login'
const tokenLogoutPath = '/auth/logout'

// Safe localStorage helpers
function getStoredProfile(): Profile | null {
  try {
    const storedProfile = localStorage.getItem(loginNameKey)
    if (!storedProfile) return null
    const parsed = JSON.parse(storedProfile) as any
    // Convert expires string back to Date object
    if (parsed.expires) {
      parsed.expires = new Date(parsed.expires)
    }
    return parsed as Profile
  } catch (error) {
    Sentry.captureException(error)
    return null
  }
}

function setStoredProfile(profile: Profile): void {
  try {
    localStorage.setItem(loginNameKey, JSON.stringify(profile))
  } catch (error) {
    Sentry.captureException(error)
  }
}

class AuthService {
  idToken: string | null = null
  accessToken: string | null = null
  profile: Profile | null = null
  tokenExpiry: Date | null = null
  accessTokenExpiry: Date | null = null
  isAlternateToken = false

  login(customState?: any): void {
    webAuth.authorize({
      appState: customState,
    })
  }



  logout(): void {
    localStorage.removeItem(loginStatusKey)

    this.idToken = null
    this.accessToken = null
    this.tokenExpiry = null
    this.profile = null
    this.accessTokenExpiry = null
    this.isAlternateToken = false

    // Reset Pinia store
    const store = useAppStore()
    store.resetState()

    if (this.isAlternateLogin()) {
      this.logoutAlternate()
    } else {
      webAuth.logout({
        returnTo: `${window.location.origin}`,
      })
    }
    localStorage.removeItem(loginNameKey)
    localStorage.removeItem(loginStatusKey)
  }

  logoutAlternate(): Promise<Response> {
    return fetch(
      import.meta.env.VITE_REVENUE_API_URL + tokenLogoutPath,
      {
        method: 'POST',
        credentials: 'include',
      }
    )
  }

  handleAuthentication(alternateToken?: string, memberId?: string): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      if (alternateToken && memberId) {
        localStorage.setItem(loginStatusKey, 'alternate')
        fetch(
          import.meta.env.VITE_REVENUE_API_URL + tokenLoginPath,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ token: alternateToken, identifier: memberId })
          }
        )
          .then((response) => {
            if (!response.ok) {
              throw new Error(`HTTP error! status: ${response.status}`)
            }
            this.localLogin({ memberId })
            sessionStorage.setItem(dataFromTokenKey, alternateToken.substr(0, 6))
            resolve({ memberId })
          })
          .catch((err: any) => {
            Sentry.captureException(err)
            if (this.isAuthenticated()) {
              this.logout()
            }
            reject(err)
          })
      } else {
        webAuth.parseHash((err: auth0.Auth0ParseHashError | null, authResult: auth0.Auth0DecodedHash | null) => {
          if (err) {
            Sentry.captureException(err)
            reject(err)
          } else if (authResult && authResult.idToken) {
            const mappedResult: AuthResult = {
              idToken: authResult.idToken,
              accessToken: authResult.accessToken || undefined,
              idTokenPayload: authResult.idTokenPayload,
              expiresIn: authResult.expiresIn || undefined,
            }
            this.logoutAlternate().then(() => {
              this.localLogin(mappedResult)
              resolve(mappedResult)
            }).catch(logoutErr => {
              Sentry.captureException(logoutErr)
              this.localLogin(mappedResult)
              resolve(mappedResult)
            })
          } else {
            reject(new Error('No authentication result received'))
          }
        })
      }
    })
  }

  private mapProfileToUser(profile: Profile): User {
    return {
      msToExpire: profile.expires ? profile.expires.getTime() : undefined,
      name: profile.name,
      sub: profile.sub,
      email: profile.email,
      email_verified: profile.email_verified,
      picture: profile.picture,
      nickname: profile.nickname,
    }
  }

  isAuthenticated(): boolean {
    const storedProfile = getStoredProfile()
    if (storedProfile) {
      this.profile = storedProfile
    }
    if (this.isAlternateLogin()) {
      const store = useAppStore()
      if (this.profile) {
        store.setAuthenticatedUser(this.mapProfileToUser(this.profile))
      }
      return true
    }
    return localStorage.getItem(loginStatusKey) === 'true'
  }

  isIdTokenValid(): boolean {
    return (
      !!this.idToken && !!this.tokenExpiry && Date.now() < this.tokenExpiry.getTime()
    )
  }

  isAccessTokenValid(): boolean {
    return (
      !!this.accessToken &&
      !!this.accessTokenExpiry &&
      Date.now() < this.accessTokenExpiry.getTime()
    )
  }

  getUser(): Profile | null {
    return this.profile
  }

  getAccessToken(): Promise<string | undefined> {
    return new Promise((resolve) => {
      if (this.isAccessTokenValid()) {
        resolve(this.accessToken!)
      } else if (this.isAlternateLogin() === false) {
        this.renewTokens()
          .then((authResult) => {
            resolve(authResult.accessToken)
          })
          .catch(() => {
            // Handle error silently
          })
      }
    })
  }

  localLogin(authResult: AuthResult): void {
    const store = useAppStore()
    
    if (authResult.memberId) {
      this.profile = {
        name: authResult.memberId,
        expires: new Date(Date.now() + 1000 * 60 * 60),
      }
      localStorage.setItem(loginStatusKey, 'alternate')
      setStoredProfile(this.profile)
      store.setAuthenticatedUser(this.mapProfileToUser(this.profile))
    } else if (authResult.idToken && authResult.idTokenPayload) {
      this.idToken = authResult.idToken
      this.profile = authResult.idTokenPayload
      this.accessToken = authResult.accessToken || null

      // Convert the expiry time from seconds to milliseconds,
      // required by the Date constructor
      this.tokenExpiry = this.profile?.exp ? new Date(this.profile.exp * 1000) : null

      // Convert expiresIn to milliseconds and add the current time
      // (expiresIn is a relative timestamp, we want an absolute time)
      this.accessTokenExpiry = new Date(Date.now() + authResult.expiresIn! * 1000)
      sessionStorage.removeItem(dataFromTokenKey)
      localStorage.setItem(loginStatusKey, 'true')
      store.setAuthenticatedUser(this.mapProfileToUser(authResult.idTokenPayload))
    } else {
      throw new Error('Invalid authentication result: missing required data')
    }
  }

  renewTokens(): Promise<AuthResult> {
    return new Promise((resolve, reject) => {
      const loginStatus = localStorage.getItem(loginStatusKey)
      if (loginStatus !== 'true') {
        this.isAuthenticated()
        reject(new Error('Not logged in'))
      } else {
        webAuth.checkSession({}, (err: auth0.Auth0Error | null, authResult: auth0.Auth0DecodedHash | null) => {
          if (err) {
            Sentry.withScope((scope) => {
              scope.setExtra('authResult', authResult)
              scope.setExtra(loginStatusKey, loginStatus)
              Sentry.captureException(err)
            })
            this.logout()
            reject(err)
          } else if (loginStatus !== 'true') {
            reject(new Error(`Not logged in. loginStatus is ${loginStatus}`))
          } else if (authResult) {
            const mappedResult: AuthResult = {
              idToken: authResult.idToken,
              accessToken: authResult.accessToken || undefined,
              idTokenPayload: authResult.idTokenPayload,
              expiresIn: authResult.expiresIn || undefined,
            }
            this.localLogin(mappedResult)
            resolve(mappedResult)
          } else {
            reject(new Error('Invalid authentication result'))
          }
        })
      }
    })
  }

  isAlternateLogin(): boolean {
    if (
      this.profile &&
      this.profile.expires &&
      Date.now() > this.profile.expires.getTime()
    ) {
      localStorage.removeItem(loginStatusKey)
      localStorage.removeItem(loginNameKey)
    }
    return localStorage.getItem(loginStatusKey) === 'alternate'
  }

  async loginExpiration(): Promise<Date | undefined> {
    try {
      const isLoginAlternate = this.isAlternateLogin()
      let msToExpire: Date

      if (isLoginAlternate) {
        const storedProfile = getStoredProfile()
        if (!storedProfile?.expires) {
          return undefined
        }
       
        msToExpire = storedProfile.expires
      } else {
        const accessToken = await this.getAccessToken()
        if (!accessToken) {
          return undefined
        }
        try {
          const tokenParts = accessToken.split('.')
          if (tokenParts.length !== 3) {
            throw new Error('Invalid token format')
          }
          const parsedToken = JSON.parse(atob(tokenParts[1])) as { exp?: number }
          if (!parsedToken.exp) {
            return undefined
          }
          msToExpire = new Date(parsedToken.exp * 1000)
        } catch (tokenError) {
          Sentry.captureException(tokenError)
          return undefined
        }
      }
      return new Date(msToExpire)
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setExtra('isAlternateLogin', this.isAlternateLogin())
        Sentry.captureException(e)
      })
      return undefined
    }
  }
}

const AuthEngine = new AuthService()
export default AuthEngine